import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { label: "首页", path: "/" },
  { label: "电影", path: "/movie" },
  { label: "电视剧", path: "/tv" },
  { label: "资讯", path: "/news" },
  { label: "关于", path: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur"
    >
      <div className="h-1 w-full bg-gradient-to-r from-primary-700 via-secondary-500 to-accent-500" />
      <div className="mx-auto flex h-16 items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50">
            <i className="ri-film-line text-lg" />
          </span>
          <span className="font-heading text-lg md:text-xl font-bold tracking-tight text-primary-800 whitespace-nowrap">
            飘花<span className="text-accent-600">电影网</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-2">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(item.path)
                  ? "bg-primary-100 text-primary-800"
                  : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={handleSearch} className="ml-auto hidden sm:flex items-center md:w-72">
          <div className="relative w-full">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索电影、电视剧、资讯"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <button
            type="submit"
            className="ml-2 hidden whitespace-nowrap rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 lg:block"
          >
            搜索
          </button>
        </form>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMenuOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={handleSearch} className="mb-3 sm:hidden">
            <div className="relative w-full">
              <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="搜索电影、电视剧、资讯"
                className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-primary-500"
              />
            </div>
          </form>
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`rounded-md px-3 py-2.5 text-sm font-medium ${
                  isActive(item.path)
                    ? "bg-primary-100 text-primary-800"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}